import React, { useState, useEffect, useRef, useMemo } from "react";
import { formatNumber, toFixed2 } from "utils/formators";
import { Skeleton } from "campaign-builder/ui";
import { StarIcon } from "components/icons/components/Star";
import { ResidencesIcon } from "components/icons/components/ResidencesIcon";
import { RouteIcon } from "components/icons/components/RouteIcon";
import cn from "classnames";
import { useEDDMCostsCalculationV2 } from "Hooks/useEDDMCostsCalculationV2";
import { getTooltipPosition } from "./utils";

// Tooltip "beak" carets. Inline SVGs (using the original MUI ArrowDrop/Arrow
// triangle paths) filled with `currentColor` so the pointer matches the card
// surface and inverts in dark mode — the wrapper sets `text-card`.
const beakBase = "absolute h-6 w-6 fill-current text-card";
const TopBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "top-[-14px]")} aria-hidden>
    <path d="M7 14l5-5 5 5z" />
  </svg>
);
const BottomBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "bottom-[-14px]")} aria-hidden>
    <path d="M7 10l5 5 5-5z" />
  </svg>
);
const RightBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "right-[-14px]")} aria-hidden>
    <path d="M10 17l5-5-5-5v10z" />
  </svg>
);
const LeftBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "left-[-14px]")} aria-hidden>
    <path d="M14 7l-5 5 5 5V7z" />
  </svg>
);

const statisticsBlockContainer =
  "cb-v2-root absolute z-[9999] flex items-center justify-around rounded-[8px] border border-solid border-transparent bg-card px-[18px] py-2 shadow-[0px_2px_6px_0px_#27375414] pointer-events-none dark:border-border";
const statisticsSection = "flex flex-col items-start justify-between";
const statisticsAmount = "flex items-center text-sm font-bold text-[#222336] dark:text-foreground [&_svg]:mr-[3px]";
const statisticsLabel = "text-xs text-[#0C172C] dark:text-foreground";
const verticalSeparator = "ml-3 mr-4 h-[25px] w-px bg-[#E2E2FC] dark:bg-border";

export const AreaStatisticsV2 = ({
  isCalculatingRoutesIntercection,
  intersectedRoutesRef,
  mousePositionCoords,
  selectedTargets,
}) => {
  const { calculateIntercectedRoutesCost } = useEDDMCostsCalculationV2();
  const [isOpen, setIsOpen] = useState(false);
  const [totalCost, setTotalCost] = useState(null);
  const [fitScore, setFitScore] = useState(null);
  const [blockData, setBlockData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [tooltipArrowPosition, setTooltipArrowPosition] = useState("bottom");

  const containerRef = useRef(null);
  const currentContainerRefBounds = useMemo(
    () => containerRef.current?.getBoundingClientRect(),
    [containerRef.current]
  );

  useEffect(() => {
    if (isCalculatingRoutesIntercection) {
      setBlockData(null);
      setTotalCost(null);
    } else if (!isCalculatingRoutesIntercection && !!intersectedRoutesRef.current) {
      const { cost, business, routes, flyers, fitScore } = calculateIntercectedRoutesCost(
        [...intersectedRoutesRef.current.values()],
        selectedTargets
      );
      setBlockData({ BUSINESS_C: business, HOME_COUNT: flyers, APT_COUNT: 0 });
      setTotalCost(cost);
      setFitScore(fitScore);
    }
  }, [isCalculatingRoutesIntercection, intersectedRoutesRef.current]);

  useEffect(() => {
    if (containerRef.current && mousePositionCoords) {
      setTooltipPosition(null);
      const { arrowPosition, newTooltipPosition } = getTooltipPosition({ containerRef, mousePositionCoords });
      setTooltipArrowPosition(arrowPosition);
      setTooltipPosition(newTooltipPosition);
    }
  }, [mousePositionCoords, currentContainerRefBounds]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const getAddressesCount = () => {
    if (isCalculatingRoutesIntercection) {
      return <Skeleton className="h-5 w-10" />;
    } else if (!isCalculatingRoutesIntercection && blockData) {
      return formatNumber(blockData.HOME_COUNT + blockData.APT_COUNT);
    }
  };
  const getTotalCost = () => {
    if (isCalculatingRoutesIntercection) {
      return <Skeleton className="h-5 w-10" />;
    } else if (!isCalculatingRoutesIntercection && typeof totalCost === "number") {
      return (
        <>
          <span>$</span> <span className="ml-[3px]">{formatNumber(toFixed2(totalCost))}</span>
        </>
      );
    }
  };

  const getFitScore = () => {
    if (isCalculatingRoutesIntercection) {
      return <Skeleton className="h-5 w-10" />;
    } else if (!isCalculatingRoutesIntercection && typeof fitScore === "string") {
      return (
        <>
          <span className="ml-[3px]">{fitScore}</span>
        </>
      );
    }
  };

  const getTotalRoutes = () => {
    if (isCalculatingRoutesIntercection) {
      return <Skeleton className="h-5 w-5" />;
    } else {
      return intersectedRoutesRef?.current?.size || 0;
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn(statisticsBlockContainer, "mob-hidden")}
      style={{
        top: `${tooltipPosition?.y || 0}px`,
        left: `${tooltipPosition?.x || 0}px`,
        display: !tooltipPosition ? "none" : "flex",
      }}
    >
      {tooltipArrowPosition === "bottom" && <TopBeak />}
      {tooltipArrowPosition === "top" && <BottomBeak />}
      {tooltipArrowPosition === "right" && <RightBeak />}
      {tooltipArrowPosition === "left" && <LeftBeak />}
      {!!selectedTargets?.length && (
        <>
          <div className={statisticsSection}>
            <span className={statisticsLabel}>Fit score</span>
            <span className={statisticsAmount}>
              <StarIcon stroke="currentColor" /> {getFitScore()}
            </span>
          </div>
          <div className={verticalSeparator} />
        </>
      )}
      <div className={statisticsSection}>
        <span className={statisticsLabel}>Routes</span>
        <span className={statisticsAmount}>
          <RouteIcon stroke="currentColor" /> {getTotalRoutes()}
        </span>
      </div>
      <div className={verticalSeparator} />
      <div className={statisticsSection}>
        <span className={statisticsLabel}>Residences</span>
        <span className={statisticsAmount}>
          <ResidencesIcon stroke="currentColor" /> {getAddressesCount()}
        </span>
      </div>
      <div className={verticalSeparator} />
      <div className={statisticsSection}>
        <span className={statisticsLabel}>Costs</span>
        <span className={statisticsAmount}>{getTotalCost()}</span>
      </div>
    </div>
  );
};
